import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  CreditCard,
  Sparkles,
  Search,
  X,
  SlidersHorizontal,
  Calendar,
  ArrowUpDown,
  RotateCcw,
  BookOpen,
  QrCode,
} from "lucide-react";
import ConfirmUpiModal from "./ConfirmUpiModal";
import { CustomerTransaction } from "../../../../Data/transaction.dto";
import MarketplaceEmptyState, {
  MarketplaceLoadingSpinner,
} from "../../../organisms/Dashboard/Marketplace/MarketplaceEmptyState";
import {
  ListPanel,
  ListHeader,
  ListTitle,
  CountBadge,
  TableWrap,
  PaymentsTable,
  AmountCell,
  StatusPill,
} from "./transactionTableStyles";
import styled from "styled-components";
import Pagination from "../HomePage/Pagination";
import { useAppDispatch } from "../../../../Redux/Hooks";
import { getCustomerTransactionAPI } from "../../../../Redux/ApiCalls/Dashboard/CustomerTransationApi";

const gradient =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.9) 0%, rgba(206, 86, 176, 0.9) 100%)";

function formatAmount(amount: number) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(iso?: string) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

interface TransactionHistoryProps {
  transactions: CustomerTransaction[];
  loading?: boolean;
  count?: number;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  paymentType?: "general" | "album";
}

const TransactionHistory = ({
  transactions,
  loading,
  count,
  currentPage,
  totalPages,
  onPageChange,
  paymentType = "general",
}: TransactionHistoryProps) => {
  const dispatch = useAppDispatch();
  const isInitialMount = useRef(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [selectedPendingUpi, setSelectedPendingUpi] = useState<CustomerTransaction | null>(null);

  const isAlbum = paymentType === "album";

  const isFilterActive =
    searchQuery.trim() !== "" ||
    statusFilter !== "all" ||
    dateFilter !== "all" ||
    sortBy !== "newest";

  // Reset filters when switching payment tab
  useEffect(() => {
    setSearchQuery("");
    setStatusFilter("all");
    setDateFilter("all");
    setSortBy("newest");
  }, [paymentType]);

  // Fetch all records without pagination when search or filter is active
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (isFilterActive) {
      const timeout = setTimeout(() => {
        dispatch(
          getCustomerTransactionAPI({
            page: 1,
            page_size: 1000,
            ...(isAlbum ? { album_only: true } : { general_only: true }),
            ...(searchQuery.trim() ? { search: searchQuery.trim() } : {}),
            ...(statusFilter !== "all" ? { status: statusFilter } : {}),
          })
        );
      }, 300);
      return () => clearTimeout(timeout);
    } else {
      dispatch(
        getCustomerTransactionAPI({
          page: currentPage || 1,
          page_size: 10,
          ...(isAlbum ? { album_only: true } : { general_only: true }),
        })
      );
    }
  }, [searchQuery, statusFilter, dateFilter, sortBy, isAlbum, isFilterActive, dispatch]);

  const handleResetFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setDateFilter("all");
    setSortBy("newest");
  };

  // Filtered and sorted transactions
  const filteredTransactions = useMemo(() => {
    return transactions
      .filter((tx) => {
        // 1. Search filter
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase().trim();
          const fullName = [tx.first_name, tx.last_name].filter(Boolean).join(" ").toLowerCase();
          const customerName = (tx.customer_name || "").toLowerCase();
          const contact = (tx.contact_details || "").toLowerCase();
          const album = (tx.album_name || "").toLowerCase();
          const desc = (tx.description || "").toLowerCase();
          const id = String(tx.id || (tx as any).payment_id || "").toLowerCase();

          const matches =
            fullName.includes(q) ||
            customerName.includes(q) ||
            contact.includes(q) ||
            album.includes(q) ||
            desc.includes(q) ||
            id.includes(q);

          if (!matches) return false;
        }

        // 2. Status filter
        if (statusFilter !== "all") {
          const s = (tx.status || "").toLowerCase();
          if (statusFilter === "paid") {
            const isPaid = ["paid", "captured", "completed", "active", "success"].includes(s);
            if (!isPaid) return false;
          } else if (statusFilter === "created") {
            const isCreated = ["created", "pending", "user_dropped"].includes(s);
            if (!isCreated) return false;
          } else if (statusFilter === "failed") {
            const isFailed = ["failed", "expired", "cancelled", "inactive"].includes(s);
            if (!isFailed) return false;
          }
        }

        // 3. Date filter
        if (dateFilter !== "all" && tx.created_at) {
          const txDate = new Date(tx.created_at);
          const now = new Date();

          if (dateFilter === "today") {
            const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            if (txDate < startOfToday) return false;
          } else if (dateFilter === "7days") {
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(now.getDate() - 7);
            if (txDate < sevenDaysAgo) return false;
          } else if (dateFilter === "30days") {
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(now.getDate() - 30);
            if (txDate < thirtyDaysAgo) return false;
          } else if (dateFilter === "this_month") {
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            if (txDate < startOfMonth) return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        if (sortBy === "newest") {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateB - dateA;
        } else if (sortBy === "oldest") {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateA - dateB;
        } else if (sortBy === "amount_high") {
          return (b.amount || 0) - (a.amount || 0);
        } else if (sortBy === "amount_low") {
          return (a.amount || 0) - (b.amount || 0);
        }
        return 0;
      });
  }, [transactions, searchQuery, statusFilter, dateFilter, sortBy]);

  const displayCount = count !== undefined ? count : transactions.length;

  const headers = isAlbum
    ? ["Customer name", "Contact", "Album", "Amount", "Status", "Created"]
    : ["First name", "Last name", "Contact", "Amount", "Status", "Created"];

  return (
    <ListPanel>
      <ListHeader>
        <ListTitle>
          {isAlbum ? "Existing customer payment transactions" : "New customer payment transactions"}
        </ListTitle>
        {displayCount > 0 && (
          <CountBadge>
            {isFilterActive
              ? `${filteredTransactions.length} record${filteredTransactions.length !== 1 ? "s" : ""} found`
              : `${displayCount} record${displayCount !== 1 ? "s" : ""}`}
          </CountBadge>
        )}
      </ListHeader>

      {/* Search & Filters Bar */}
      <FilterControlBar>
        <SearchInputBox>
          <SearchIconWrap>
            <Search size={16} />
          </SearchIconWrap>
          <SearchInputField
            type="text"
            placeholder={
              isAlbum
                ? "Search by customer name, mobile, album name..."
                : "Search by customer name, mobile, description..."
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <ClearSearchBtn type="button" onClick={() => setSearchQuery("")} aria-label="Clear search">
              <X size={14} />
            </ClearSearchBtn>
          )}
        </SearchInputBox>

        <FilterGroupRow>
          {/* Status Filter */}
          <SelectWrapper>
            <SelectIconWrap>
              <SlidersHorizontal size={14} />
            </SelectIconWrap>
            <FilterSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="paid">Paid / Captured</option>
              <option value="created">Created</option>
              <option value="failed">Failed / Expired</option>
            </FilterSelect>
          </SelectWrapper>

          {/* Date Filter */}
          <SelectWrapper>
            <SelectIconWrap>
              <Calendar size={14} />
            </SelectIconWrap>
            <FilterSelect
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="this_month">This Month</option>
            </FilterSelect>
          </SelectWrapper>

          {/* Sort By */}
          <SelectWrapper>
            <SelectIconWrap>
              <ArrowUpDown size={14} />
            </SelectIconWrap>
            <FilterSelect
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="amount_high">Amount: High to Low</option>
              <option value="amount_low">Amount: Low to High</option>
            </FilterSelect>
          </SelectWrapper>

          {/* Reset button if filter is active */}
          {isFilterActive && (
            <ResetButton type="button" onClick={handleResetFilters} title="Reset all filters">
              <RotateCcw size={13} />
              <span>Reset</span>
            </ResetButton>
          )}
        </FilterGroupRow>
      </FilterControlBar>

      {/* Active Filter Chips */}
      {isFilterActive && (
        <ActiveChipsRow>
          <ActiveChipsLabel>Active filters:</ActiveChipsLabel>
          {searchQuery.trim() && (
            <FilterChip>
              <span>Search: "{searchQuery.trim()}"</span>
              <button type="button" onClick={() => setSearchQuery("")}>
                <X size={12} />
              </button>
            </FilterChip>
          )}
          {statusFilter !== "all" && (
            <FilterChip>
              <span>
                Status: {statusFilter === "paid" ? "Paid / Captured" : statusFilter === "created" ? "Created" : "Failed / Expired"}
              </span>
              <button type="button" onClick={() => setStatusFilter("all")}>
                <X size={12} />
              </button>
            </FilterChip>
          )}
          {dateFilter !== "all" && (
            <FilterChip>
              <span>
                Date: {dateFilter === "today" ? "Today" : dateFilter === "7days" ? "Last 7 Days" : dateFilter === "30days" ? "Last 30 Days" : "This Month"}
              </span>
              <button type="button" onClick={() => setDateFilter("all")}>
                <X size={12} />
              </button>
            </FilterChip>
          )}
          {sortBy !== "newest" && (
            <FilterChip>
              <span>
                Sort: {sortBy === "oldest" ? "Oldest" : sortBy === "amount_high" ? "Amount (High-Low)" : "Amount (Low-High)"}
              </span>
              <button type="button" onClick={() => setSortBy("newest")}>
                <X size={12} />
              </button>
            </FilterChip>
          )}
          <ClearAllLink type="button" onClick={handleResetFilters}>
            Clear all
          </ClearAllLink>
        </ActiveChipsRow>
      )}

      {/* Table Content or Empty State */}
      {loading ? (
        <MarketplaceLoadingSpinner padding="60px 22px" />
      ) : transactions.length === 0 ? (
        <MarketplaceEmptyState
          padding="32px 0 24px"
          icon={<CreditCard size={36} strokeWidth={1.5} />}
          title={isAlbum ? "No existing customer payments found" : "No new customer payments found"}
          description={
            isAlbum
              ? "Payment history for existing customer payments will appear here."
              : "Payment history for new customer payments will appear here."
          }
          hint={{
            icon: <Sparkles size={13} />,
            text: "Captured, created & failed payments show up in this list",
          }}
        />
      ) : filteredTransactions.length === 0 ? (
        <EmptyFilteredState>
          <Search size={32} color="#c94daa" />
          <EmptyFilteredTitle>No matching transactions found</EmptyFilteredTitle>
          <EmptyFilteredDesc>
            No transactions match your current search query or filter criteria. Try adjusting or clearing your filters.
          </EmptyFilteredDesc>
          <ClearFilterBtn type="button" onClick={handleResetFilters}>
            <RotateCcw size={14} />
            <span>Clear Filters</span>
          </ClearFilterBtn>
        </EmptyFilteredState>
      ) : (
        <TableWrap>
          <PaymentsTable $minWidth={isAlbum ? 740 : 700}>
            <thead>
              <tr>
                {headers.map((h) => (
                  <th key={h}><span>{h}</span></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.map((tx, idx) => (
                <tr key={tx.id || (tx as any).payment_id || idx}>
                  {isAlbum ? (
                    <td>{tx.customer_name || [tx.first_name, tx.last_name].filter(Boolean).join(" ") || "—"}</td>
                  ) : (
                    <>
                      <td>{tx.first_name || tx.customer_name || "—"}</td>
                      <td>{tx.last_name || "—"}</td>
                    </>
                  )}
                  <td>{tx.contact_details || "—"}</td>
                  {isAlbum && (
                    <td>
                      {tx.album_name ? (
                        <AlbumTag>
                          <BookOpen size={11} />
                          <span>{tx.album_name}</span>
                        </AlbumTag>
                      ) : (
                        <DashText>—</DashText>
                      )}
                    </td>
                  )}
                  <td><AmountCell>{formatAmount(tx.amount)}</AmountCell></td>
                  <td>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                      <StatusPill $status={tx.status}>
                        {tx.status ? tx.status.charAt(0).toUpperCase() + tx.status.slice(1) : "—"}
                      </StatusPill>
                      {tx.payment_method === "upi_direct" &&
                        (tx.status === "pending" || tx.status === "created") && (
                          <button
                            type="button"
                            onClick={() => setSelectedPendingUpi(tx)}
                            style={{
                              marginTop: 2,
                              padding: "2px 8px",
                              borderRadius: 6,
                              border: "1px solid rgba(49, 166, 106, 0.4)",
                              background: "#f0fdf4",
                              color: "#166534",
                              fontSize: 10.5,
                              fontWeight: 700,
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 3,
                            }}
                          >
                            Confirm UTR
                          </button>
                        )}
                    </div>
                  </td>
                  <td>{formatDate(tx.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </PaymentsTable>
        </TableWrap>
      )}

      {selectedPendingUpi && (
        <ConfirmUpiModal
          isOpen={Boolean(selectedPendingUpi)}
          onClose={() => setSelectedPendingUpi(null)}
          onSuccess={() => {
            setSelectedPendingUpi(null);
            dispatch(
              getCustomerTransactionAPI({
                page: currentPage,
                page_size: 10,
                ...(isAlbum ? { album_only: true } : { general_only: true }),
              })
            );
          }}
          paymentId={selectedPendingUpi.id || (selectedPendingUpi as any).payment_id}
          amount={selectedPendingUpi.amount}
          customerName={
            selectedPendingUpi.customer_name ||
            `${selectedPendingUpi.first_name || ""} ${selectedPendingUpi.last_name || ""}`.trim()
          }
          referenceId={selectedPendingUpi.reference_id}
        />
      )}

      {!isFilterActive && totalPages !== undefined && totalPages > 1 && onPageChange && currentPage && (
        <PaginationWrapper>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </PaginationWrapper>
      )}
    </ListPanel>
  );
};

export default TransactionHistory;

/* Styled Components */

const FilterControlBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchInputBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 260px;
  max-width: 440px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  transition: all 0.15s ease;

  &:focus-within {
    background: #ffffff;
    border-color: #c94daa;
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.12);
  }

  @media (max-width: 900px) {
    max-width: 100%;
  }
`;

const SearchIconWrap = styled.div`
  padding-left: 12px;
  display: flex;
  align-items: center;
  color: rgba(23, 23, 23, 0.4);
`;

const SearchInputField = styled.input`
  width: 100%;
  padding: 10px 12px 10px 8px;
  border: none;
  background: transparent;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: #171717;
  outline: none;

  &::placeholder {
    color: rgba(23, 23, 23, 0.4);
  }
`;

const ClearSearchBtn = styled.button`
  background: transparent;
  border: none;
  padding: 6px;
  margin-right: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.4);
  border-radius: 50%;
  transition: all 0.15s ease;

  &:hover {
    color: #171717;
    background: rgba(23, 23, 23, 0.08);
  }
`;

const FilterGroupRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    width: 100%;
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SelectIconWrap = styled.div`
  position: absolute;
  left: 10px;
  pointer-events: none;
  display: flex;
  align-items: center;
  color: rgba(23, 23, 23, 0.45);
`;

const FilterSelect = styled.select`
  appearance: none;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 10px;
  padding: 8px 28px 8px 30px;
  font-family: Montserrat, sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: #171717;
  cursor: pointer;
  outline: none;
  transition: all 0.15s ease;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='rgba(23,23,23,0.5)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;

  &:hover {
    border-color: rgba(201, 77, 170, 0.35);
  }

  &:focus {
    border-color: #c94daa;
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background-color: #ffffff;
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const ResetButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid rgba(201, 77, 170, 0.2);
  background: #fdf2f8;
  color: #c94daa;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #fce7f3;
    border-color: #c94daa;
  }

  @media (max-width: 600px) {
    grid-column: span 2;
    justify-content: center;
  }
`;

const ActiveChipsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(23, 23, 23, 0.02);
  border: 1px dashed rgba(23, 23, 23, 0.08);
`;

const ActiveChipsLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const FilterChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 6px;
  background: rgba(201, 77, 170, 0.08);
  border: 1px solid rgba(201, 77, 170, 0.2);
  font-family: Montserrat, sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  color: #c94daa;

  button {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: #c94daa;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: #9d174d;
    }
  }
`;

const ClearAllLink = styled.button`
  background: none;
  border: none;
  font-family: Montserrat, sans-serif;
  font-size: 11.5px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.6);
  cursor: pointer;
  padding: 2px 6px;
  text-decoration: underline;

  &:hover {
    color: #c94daa;
  }
`;

const EmptyFilteredState = styled.div`
  padding: 36px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  background: #fafafa;
  border-radius: 14px;
  border: 1px dashed rgba(23, 23, 23, 0.1);
`;

const EmptyFilteredTitle = styled.h3`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
`;

const EmptyFilteredDesc = styled.p`
  margin: 0;
  max-width: 400px;
  font-family: Montserrat, sans-serif;
  font-size: 12.5px;
  color: rgba(23, 23, 23, 0.6);
  line-height: 1.45;
`;

const ClearFilterBtn = styled.button`
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 10px;
  border: none;
  background: ${gradient};
  color: #ffffff;
  font-family: Montserrat, sans-serif;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(206, 86, 176, 0.25);
  transition: all 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(206, 86, 176, 0.35);
  }
`;

const AlbumTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  background: rgba(201, 77, 170, 0.1);
  color: #c94daa;
`;

const DashText = styled.span`
  color: rgba(23, 23, 23, 0.35);
  font-size: 13px;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`;
